import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet"; 

// Components
import SurveyProgressOverlay from "./SurveyProgressOverlay";
import VectorLeaflet from "./VectorLeaflet"; 
import ImageLeaflet from "./ImageLeaflet"; 
import OrthoLayer from "./OrthoLayer"; 
import Raster3dLayer from "./3dLayer"; 
import NdviLayer from "./ndviLayer";
import CesiumLayer from "./CesiumLayer";

// --- IMPORT THE NEW DRAWING COMPONENT ---
import UserVectorLeaflet from "./UserVectorLeaflet"; 

// --- Configuration ---
// --- Configuration ---
const API_BASE = (
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "/api" // This is the crucial fallback for your proxy
).replace(/\/+$/, "");

// Layout Constants
const SIDEBAR_WIDTH = 280;
const MAP_SELECTOR_EXTRA_OFFSET = 20; 

// --- Helper Functions ---
const normalizeNameKey = (val) => (String(val ?? "").trim().toLowerCase());

function MapInner({
  geojson,
  tile,
  latestSurvey,
  apiBase,
  photoLayerVisible,
}) {
  const map = useMap();
  const [panesReady, setPanesReady] = useState(false);

  // Pane names
  const baseTilePaneName = "baseTilePane";
  const boundaryPaneName = "boundaryPane";
  const overlayPaneName = "overlayPane";
  const markerPaneName = "markerPane";

  // --- Create Panes ---
  useEffect(() => {
    if (!map) return;
    try {
      if (!map.getPane(baseTilePaneName)) map.createPane(baseTilePaneName);
      if (!map.getPane(boundaryPaneName)) map.createPane(boundaryPaneName);
      if (!map.getPane(overlayPaneName)) map.createPane(overlayPaneName);
      if (!map.getPane(markerPaneName)) map.createPane(markerPaneName);

      map.getPane(baseTilePaneName).style.zIndex = "200";
      map.getPane(boundaryPaneName).style.zIndex = "300";
      map.getPane(overlayPaneName).style.zIndex = "400";
      map.getPane(markerPaneName).style.zIndex = "600";

      map.getPane(baseTilePaneName).style.pointerEvents = "none";
      map.getPane(boundaryPaneName).style.pointerEvents = "auto";
      map.getPane(overlayPaneName).style.pointerEvents = "auto";
      map.getPane(markerPaneName).style.pointerEvents = "auto";

      setPanesReady(true);
    } catch (err) {
      console.warn("Pane creation warning:", err);
    }
  }, [map]);

  // --- Auto-Zoom Logic (FIXED) ---
  useEffect(() => {
    // 1. If no geojson or map, stop.
    if (!geojson || !map) return;

    // 2. We removed the 'hasInitialZoom' check so this runs whenever 'geojson' changes.
    //    Since your parent component sets geojson to null before loading new data,
    //    this will trigger correctly for every new location.
    try {
      const layer = L.geoJSON(geojson);
      const bounds = layer.getBounds();
      
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds, {
          padding: [50, 50],
          animate: true, // Enable animation for smooth transition
          maxZoom: 18,
        });
      }
    } catch (err) {
      console.warn("[MapInner] Auto-zoom error:", err);
    }
  }, [geojson, map]); // Dependency on geojson ensures it re-runs when data updates

  if (!panesReady) return null;

  return (
    <>
      <TileLayer
        url={tile.url}
        attribution={tile.attribution}
        maxZoom={tile.maxZoom || 19}
        pane={baseTilePaneName}
      />

      {geojson && (
        <GeoJSON
          data={geojson}
          pane={boundaryPaneName}
          style={() => ({ color: "#2563eb", weight: 2, fillOpacity: 0.15 })}
          interactive={false}
        />
      )}

      <OrthoLayer latestSurvey={latestSurvey} pane={overlayPaneName} apiBase={apiBase} />
      <Raster3dLayer latestSurvey={latestSurvey} pane={overlayPaneName} apiBase={apiBase} />
      <NdviLayer latestSurvey={latestSurvey} pane={overlayPaneName} apiBase={apiBase} />

      <VectorLeaflet latestSurvey={latestSurvey} apiBase={apiBase} />

      {photoLayerVisible && (
        <ImageLeaflet latestSurvey={latestSurvey} apiBase={apiBase} />
      )}

      <UserVectorLeaflet
        latestSurvey={latestSurvey}
        apiBase={apiBase}
      />
    </>
  );
}

export default function Map() {
  const { id } = useParams();
  
  const [geojson, setGeojson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tileKey, setTileKey] = useState("street");
  const [latestSurvey, setLatestSurvey] = useState(null); 
  const [surveyHistory, setSurveyHistory] = useState([]);
  const [showCesium, setShowCesium] = useState(false);
  const [photoLayerVisible, setPhotoLayerVisible] = useState(true); 
    
  const mapRef = useRef(null);
  const overlayRef = useRef(null);
  const mapStyleRef = useRef(null);
  const [overlayHeight, setOverlayHeight] = useState(0);
  const [mapStyleHeight, setMapStyleHeight] = useState(0);
  const originalTopRef = useRef(new WeakMap());

  const TILE_OPTIONS = {
    street: {
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution: '&copy; OpenStreetMap contributors',
      maxZoom: 19,
      label: "Street",
    },
    satellite: {
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      attribution: "Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics",
      maxZoom: 19,
      label: "Satellite",
    },
    dark: {
      url: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
      maxZoom: 19,
      label: "Dark",
    },
  };

  // --- Global Event Listeners ---
  useEffect(() => {
    const handleToggle = (ev) => {
      const detail = ev?.detail ?? {};
      if (detail.id === 'ground_truth_photos_control') {
          setPhotoLayerVisible(!!detail.visible);
      }
    };
    
    const onZoomToCoord = (e) => {
        if (mapRef.current && e?.detail) {
            const { lat, lon, zoom = 18 } = e.detail;
            if (Number.isFinite(parseFloat(lat)) && Number.isFinite(parseFloat(lon))) {
                mapRef.current.setView([parseFloat(lat), parseFloat(lon)], zoom);
            }
        }
    };

    window.addEventListener("map-toggle-layer", handleToggle);
    window.addEventListener("map-zoom-to-coord", onZoomToCoord);

    return () => {
      window.removeEventListener("map-toggle-layer", handleToggle);
      window.removeEventListener("map-zoom-to-coord", onZoomToCoord);
    };
  }, []);

  // --- Helper: Survey Merging ---
  const pickLocationName = (latestOrId) => {
    const cand = latestOrId?.location_name ?? latestOrId?.name ?? latestOrId?.project_name ?? (typeof id === "string" ? id : null);
    if (!cand && latestOrId && typeof latestOrId === "object") {
      return String(latestOrId.id ?? latestOrId._id ?? "").trim() || null;
    }
    return cand != null ? String(cand).trim() || null : null;
  };

  const mergeSurveyUpdate = (newSurveyRow) => {
    if (!newSurveyRow) return;
    const normalized = {
      ...newSurveyRow,
      survey_time: newSurveyRow.survey_time ?? latestSurvey?.survey_time ?? newSurveyRow.survey_date ?? null,
    };

    const newLocName = normalizeNameKey(normalized.location_name ?? normalized.location ?? "");
    const currentLocName = normalizeNameKey(pickLocationName(latestSurvey) || pickLocationName({ id }) || "");

    if (newLocName !== currentLocName) return;

    setSurveyHistory((prev = []) => {
      const filtered = (prev || []).filter((r) => String(r.id ?? r._id ?? "") !== String(normalized.id ?? normalized._id ?? ""));
      const merged = [normalized, ...filtered];
      merged.sort((a, b) => {
         const ta = new Date(a.survey_time || a.created_at || 0).getTime();
         const tb = new Date(b.survey_time || b.created_at || 0).getTime();
         return tb - ta;
      });
      return merged;
    });
    setLatestSurvey(normalized);
  };

  // Listener: Survey Created
  useEffect(() => {
    const onSurveyCreated = (ev) => { if (ev?.detail) mergeSurveyUpdate(ev.detail); };
    window.addEventListener("survey-request-created", onSurveyCreated);
    return () => window.removeEventListener("survey-request-created", onSurveyCreated);
  }, [latestSurvey, id]);

  // --- Helper: Manual Jump (From Sidebar) ---
  const fitBoundsFromGeojson = (gj) => {
    if (!gj || !mapRef.current) return;
    try {
      const layer = L.geoJSON(gj);
      const bounds = layer.getBounds();
      if (bounds && bounds.isValid()) {
        mapRef.current.fitBounds(bounds, { padding: [50, 50], animate: true });
      }
    } catch (e) {
      console.warn("Manual jump failed", e);
    }
  };

  // --- Main Data Fetching ---
  useEffect(() => {
    let cancelled = false;
    async function loadGeoAndSurvey() {
      setLoading(true);
      setGeojson(null); 
      setLatestSurvey(null);
      setSurveyHistory([]);

      const userOrg = (localStorage.getItem("organization") || "").trim();
      const orgQueryList = userOrg ? `?organization=${encodeURIComponent(userOrg)}` : "";
      const token = localStorage.getItem("token");

      const apiHeaders = { "Content-Type": "application/json", "Accept": "application/json" };
      if (token) apiHeaders.Authorization = `Bearer ${token}`;
      const fileHeaders = { "Accept": "application/geo+json, application/json" };
      if (token) fileHeaders.Authorization = `Bearer ${token}`;

      try {
        const listRes = await fetch(`${API_BASE}/project_mapx${orgQueryList}`, { headers: apiHeaders });
        if (!listRes.ok) throw new Error("Failed fetching projects");
        const list = await listRes.json();
        
        const param = String(id ?? "").toLowerCase(); 
        let project = list.find((p) => String(p.location_name ?? p.name ?? p.id ?? "").toLowerCase() === param);
        
        if (!project) {
           project = list.find((p) => String(p.location_name ?? p.name ?? "").toLowerCase().includes(param));
        }

        if (!project) {
          setLoading(false);
          return;
        }

        const geoPath = project.geojson_file || project.boundary || "";
        if (geoPath) {
          let fetchUrl = geoPath;
          try { new URL(geoPath); } catch (_) { fetchUrl = `${API_BASE}/${String(geoPath).replace(/^\/+/, "")}`; }

          const geoRes = await fetch(fetchUrl, { headers: fileHeaders });
          if (geoRes.ok) {
            const gj = await geoRes.json();
            if (!cancelled) setGeojson(gj);
          }
        }

        const surveysRes = await fetch(`${API_BASE}/survey_request${orgQueryList}`, { headers: apiHeaders });
        if (surveysRes.ok) {
            const allSurveys = await surveysRes.json();
            const targetName = String(project.location_name ?? project.name ?? "").trim();
            
            let matches = allSurveys.filter(s => String(s.location_name ?? "").trim() === targetName);
            
            if (matches.length > 0) {
                 matches.sort((a, b) => {
                     const ta = new Date(a.survey_time || a.created_at || 0).getTime();
                     const tb = new Date(b.survey_time || b.created_at || 0).getTime();
                     return tb - ta;
                 });

                 if (!cancelled) {
                     setSurveyHistory(matches);
                     let shouldClear = false;
                     try {
                        const raw = sessionStorage.getItem("survey-selected-payload");
                        const p = JSON.parse(raw);
                        if (p && p.clear) shouldClear = true;
                     } catch(e) {}

                     if (!shouldClear) setLatestSurvey(matches[0]);
                 }
            }
        }
      } catch (err) {
        console.error("[Map] Load error", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadGeoAndSurvey();
    return () => { cancelled = true; };
  }, [id, API_BASE]);

  // --- Sidebar Interaction Listener ---
  useEffect(() => {
    const processPayload = (payload) => {
      if (!payload) return;
      if (payload.clear) {
        setLatestSurvey(null);
        setSurveyHistory([]);
        try { sessionStorage.removeItem("survey-selected-payload"); } catch(e){}
        return;
      }

      setLatestSurvey({ ...payload });
      if (payload.history) setSurveyHistory(payload.history);

      if (payload.jump && geojson) {
         fitBoundsFromGeojson(geojson);
      }
    };

    const onSelected = (e) => processPayload(e?.detail);
    const onCleared = () => processPayload({ clear: true });

    window.addEventListener("survey-selected", onSelected);
    window.addEventListener("survey-cleared", onCleared);
    
    try {
        const raw = sessionStorage.getItem("survey-selected-payload");
        if (raw) processPayload(JSON.parse(raw));
    } catch(e) {}

    return () => {
        window.removeEventListener("survey-selected", onSelected);
        window.removeEventListener("survey-cleared", onCleared);
    };
  }, [geojson]);

  // --- Layout Positioning ---
  useEffect(() => {
    const updateHeight = () => {
        setOverlayHeight(overlayRef.current ? overlayRef.current.offsetHeight : 0);
        setMapStyleHeight(mapStyleRef.current ? mapStyleRef.current.offsetHeight : 0);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    
    let mo = null;
    if (overlayRef.current) {
        mo = new MutationObserver(updateHeight);
        mo.observe(overlayRef.current, { childList: true, subtree: true });
    }
    
    return () => {
        window.removeEventListener("resize", updateHeight);
        if (mo) mo.disconnect();
    };
  }, []);

  // ===============================================
  // POSITIONING LOGIC (RESTORED & FIXED)
  // ===============================================
  useEffect(() => {
    const repositionControls = () => {
      const controls = document.querySelectorAll(".leaflet-control");
      const mapStyleTop = 12 + overlayHeight + MAP_SELECTOR_EXTRA_OFFSET;

      controls.forEach((c) => {
        // 1. Capture initial CSS top value
        if (!originalTopRef.current.has(c)) {
          const computedTop = parseFloat(window.getComputedStyle(c).top);
          originalTopRef.current.set(c, isNaN(computedTop) ? 10 : computedTop);
        }
        
        const base = originalTopRef.current.get(c);
        
        // Identify controls
        const isDraw = c.classList.contains("leaflet-draw") || c.classList.contains("leaflet-draw-toolbar");
        const isZoom = c.classList.contains("leaflet-control-zoom");

        let newTop;

        if (isDraw) {
           // Draw tools: Below the "Map Style" dropdown on the right
           newTop = mapStyleTop + mapStyleHeight + 10;
        } else if (isZoom) {
           // Zoom controls: Below the Survey Progress Overlay + 20px padding (MOVED DOWN)
           newTop = base + overlayHeight + 20; 
        } else {
           // Default for other controls
           newTop = base + overlayHeight;
        }

        c.style.top = `${newTop}px`;
      });
    };

    repositionControls();
    window.addEventListener("resize", repositionControls);

    // MutationObserver watches for Leaflet controls (including Draw tools)
    const wrapper = document.getElementById("map-main-wrapper");
    let observer = null;
    if (wrapper) {
        observer = new MutationObserver(() => {
            repositionControls();
        });
        observer.observe(wrapper, { childList: true, subtree: true });
    }

    return () => {
      window.removeEventListener("resize", repositionControls);
      if (observer) observer.disconnect();
    };
  }, [overlayHeight, mapStyleHeight]);
  // ===============================================


  // --- Full Screen Fix ---
  useEffect(() => {
    document.documentElement.style.height = "100%";
    document.body.style.height = "100%";
    document.body.style.margin = "0";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const STEPS = ["Requested", "Surveyed", "Processed", "Uploaded", "Verified"];
  const activeStepIndex = STEPS.findIndex(s => s.toLowerCase() === (latestSurvey?.status || "Requested").toLowerCase());

  return (
    <div id="map-main-wrapper" style={{ position: "fixed", top: 0, bottom: 0, left: SIDEBAR_WIDTH, right: SIDEBAR_WIDTH, width: `calc(100vw - ${SIDEBAR_WIDTH*2}px)`, height: "100vh", background: "#e6eef6" }}>
      
      <SurveyProgressOverlay
        title={`Survey Progress: ${id ?? "—"}`}
        activeStep={activeStepIndex === -1 ? 0 : activeStepIndex}
        steps={STEPS}
        surveyTime={latestSurvey?.survey_time}
        overlayRef={overlayRef}
        history={surveyHistory}
        latestSurvey={latestSurvey}
        locationName={pickLocationName(latestSurvey)}
        apiBase={API_BASE} 
        onSurveyUpdated={mergeSurveyUpdate}
      />

      <div ref={mapStyleRef} style={{ position: "absolute", top: `${12 + overlayHeight + MAP_SELECTOR_EXTRA_OFFSET}px`, right: 12, zIndex: 1500, background: "rgba(255,255,255,0.9)", padding: "6px 8px", borderRadius: 6, display: "flex", gap: 8, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
        <select value={tileKey} onChange={(e) => setTileKey(e.target.value)} style={{ border: "1px solid #ddd", borderRadius: 4, padding: "4px 8px" }}>
          <option value="street">Street</option>
          <option value="satellite">Satellite</option>
          <option value="dark">Dark</option>
        </select>
        <button onClick={() => setShowCesium(true)} style={{ background: "#2563eb", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600, padding: "0 10px" }}>
          3D View
        </button>
      </div>

       <MapContainer 
          center={[0, 0]} 
          zoom={2} 
          style={{ width: "100%", height: "100%" }} 
          whenCreated={(m) => { mapRef.current = m; }}
        >
          <MapInner
            tile={TILE_OPTIONS[tileKey]}
            latestSurvey={latestSurvey}
            apiBase={API_BASE}
            photoLayerVisible={photoLayerVisible}
            geojson={geojson}
          />
      </MapContainer>

      <CesiumLayer 
        visible={showCesium} 
        onClose={() => setShowCesium(false)}
        latestSurvey={latestSurvey}  
        apiBase={API_BASE}           
        id={id} 
        surveyHistory={surveyHistory} 
        onSurveyUpdated={mergeSurveyUpdate}
      />
    </div>
  );
}